import React from "react";
import { useHistory, Link } from "react-router-dom";
import { useMyContext } from "../MyContext";
import { ChevronRightIcon } from "@heroicons/react/solid";

function ProductList() {
  const contextData = useMyContext();
  const value = contextData.productObj || [];
  const history = useHistory();

  const goToHomePage = () => {
    history.push({ pathname: "/" });
  };

  return (
    <div className="w-full bg-white font-sans overflow-hidden">

      {/* Hero Section */}
      <div className="relative w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 pt-20 pb-28 md:pt-24 md:pb-36 lg:pt-28 lg:pb-40 px-6 overflow-hidden">

        {/* Ambient Radial Glow Effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center text-white space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-2 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-50">Certified Product Catalog</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight drop-shadow-md">
            Premium Piping Solutions
          </h1>
          <p className="text-sm sm:text-base md:text-lg font-normal leading-relaxed text-orange-50 opacity-95 max-w-2xl mx-auto">
            Explore our comprehensive range of BIS-certified uPVC, HDPE, and fluid management systems engineered for durability, pressure tolerance, and long-term infrastructure value.
          </p>
        </div>

        {/* Clean Solid White Curved SVG Divider */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0] z-10">
          <svg
            className="relative block w-full h-[60px] md:h-[100px]"
            viewBox="0 0 1920 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 C640,30 1280,30 1920,120 L1920,120 L0,120 Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>

      {/* Breadcrumb Section */}
      <div className="bg-white py-3.5 px-6 md:px-12 border-b border-slate-100 relative z-20">
        <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs sm:text-sm font-medium text-slate-500">
          <button
            onClick={goToHomePage}
            className="text-orange-600 hover:text-orange-700 font-semibold transition-colors cursor-pointer"
          >
            Home
          </button>
          <span className="text-slate-300 font-light">&gt;</span>
          <span className="text-slate-800">Products</span>
        </div>
      </div>

      {/* Product Grid Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 relative z-10">

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {value.map((item, index) => {
            let itemImage;
            if (item.image) {
              try {
                itemImage = require(`../image/products/${item.image}`);
              } catch (e) {
                itemImage = null;
              }
            }

            return (
              <div
                key={item.id || index}
                className="group bg-white rounded-2xl border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 overflow-hidden flex flex-col hover:-translate-y-1.5"
              >
                {/* Product Image Frame */}
                <div className="relative h-56 bg-slate-100 overflow-hidden flex-shrink-0">
                  <span className="absolute top-3 left-3 z-10 bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider uppercase border border-white/10">
                    BIS Certified
                  </span>

                  {itemImage ? (
                    <img
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                      src={itemImage}
                      alt={item.title}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full bg-slate-100 text-slate-400 font-semibold text-xs px-4 text-center">
                      {item.title}
                    </div>
                  )}
                </div>

                {/* Product Body Details */}
                <div className="p-6 flex-grow flex flex-col justify-between space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-1 group-hover:text-orange-600 transition-colors tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Key Features Pill Block */}
                  {item.advantages && item.advantages.length > 0 && (
                    <div className="pt-3 border-t border-slate-100 space-y-2">
                      <span className="text-[10px] font-bold text-orange-600 tracking-wider uppercase block">
                        Key Features
                      </span>
                      <ul className="text-xs text-slate-600 space-y-1.5">
                        {item.advantages.slice(0, 2).map((advantage, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                            <span className="truncate text-slate-600">{advantage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Link Button */}
                  <div className="pt-2">
                    <Link to={`/product/${item.id}`} className="block">
                      <button className="w-full bg-slate-900 hover:bg-orange-600 text-white font-bold py-3 px-5 rounded-xl transition-all duration-300 shadow-md flex items-center justify-center space-x-2 text-xs sm:text-sm cursor-pointer">
                        <span>View Details</span>
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State Loader */}
        {value.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-200/80 shadow-sm max-w-md mx-auto my-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-slate-500 text-sm font-medium">
              Loading product catalog...
            </p>
          </div>
        )}
      </section>

      {/* Custom Solution Call To Action */}
      <section className="bg-slate-900 py-16 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20">
            Institutional Orders
          </span>

          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Need Custom Technical Specifications?
          </h2>

          <p className="text-slate-300 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Our engineering team assists contractors, municipal agencies, and agricultural buyers with specialized piping specifications, bulk logistics, and custom solutions.
          </p>

          <div>
            <Link to="/contact">
              <button className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:opacity-95 text-white font-bold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 text-sm cursor-pointer">
                Get in Touch
              </button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default ProductList;